import type Map from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import HeatmapLayer from 'ol/layer/Heatmap';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { transformExtent } from 'ol/proj';
import { Fill, Stroke, Style, Text, Circle as CircleStyle } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
import apiClient from '@/services/api';
import workspaceService, { CompanyLayer } from '@/services/workspace.service';
import i18n from '@/i18n';
import { DEFAULT_FILL, legendItems, scaleToZoom, styleFunction, type LayerStyle } from '@/utils/layerStyle';

/**
 * A company's own layer on the map (Veramenta docs/en/60 sec.10): drawn in the browser from its features, read
 * box by box as the company - never through GeoServer, which serves only our layers. The OpenLayers layer is named
 * `co:<key>`, which is also what an analysis is asked for it by.
 *
 * <p>How it looks is its style (utils/layerStyle.ts): one look, categories, graduated colours or proportional
 * symbols are drawn by a vector layer (clustered if asked); a heatmap by a heatmap layer. The legend reads the items
 * the layer carries ({@code legendItems}).
 */

type Kind = 'vector' | 'cluster' | 'heatmap';

function kindOf(layer: CompanyLayer): Kind {
  const s = layer.style ?? {};
  if (s.mode === 'heatmap' && layer.geometry === 'point') return 'heatmap';
  if (s.cluster && layer.geometry === 'point') return 'cluster';
  return 'vector';
}

function columnOf(layer: CompanyLayer): (key: string) => string {
  const columns: Record<string, string> = { ...(layer.columns ?? {}) };
  for (const f of layer.fields ?? []) if (f.column) columns[f.key] = f.column;
  return (key) => columns[key] ?? key;
}

function find(map: Map, layer: CompanyLayer): BaseLayer | undefined {
  let found: BaseLayer | undefined;
  map.getLayers().forEach((l) => {
    if (l.get('name') === layer.name) found = l;
  });
  return found;
}

export function isShown(map: Map | null, layer: CompanyLayer): boolean {
  return !!map && !!find(map, layer)?.getVisible();
}

function sourceOf(layer: CompanyLayer): VectorSource {
  const format = new GeoJSON();
  const source = new VectorSource({
    strategy: bboxStrategy,
    loader: (extent, _resolution, projection, success, failure) => {
      const lonLat = transformExtent(extent, projection, 'EPSG:4326');
      apiClient
        .get(workspaceService.featuresUrl(layer.workspace, layer.key, lonLat))
        .then((r) => {
          const features = format.readFeatures(r.data, { dataProjection: 'EPSG:4326', featureProjection: projection });
          features.forEach((f) => f.unset('@title'));
          source.addFeatures(features);
          success?.(features);
        })
        .catch(() => {
          source.removeLoadedExtent(extent);
          failure?.();
        });
    },
  });
  return source;
}

/** The features' own source, kept when only the look changes so that nothing is read again. */
function featureSource(l: BaseLayer | undefined): VectorSource | undefined {
  if (!l) return undefined;
  const own = l.get('featureSource');
  return own instanceof VectorSource ? own : undefined;
}

function clusterStyle(layer: CompanyLayer) {
  const single = styleFunction({ style: layer.style ?? {}, geometry: layer.geometry, column: columnOf(layer), label: layer.label });
  const fill = layer.style?.fill ?? DEFAULT_FILL;
  const cache: Record<number, Style> = {};
  return (feature: FeatureLike, resolution: number) => {
    const members: FeatureLike[] = feature.get('features') ?? [];
    if (members.length === 1) return single(members[0], resolution);
    const n = members.length;
    if (!cache[n]) {
      const r = Math.min(28, 10 + Math.log2(n) * 3);
      cache[n] = new Style({
        image: new CircleStyle({ radius: r, fill: new Fill({ color: fill }), stroke: new Stroke({ color: 'rgba(255,255,255,0.85)', width: 3 }) }),
        text: new Text({ text: String(n), font: 'bold 11px sans-serif', fill: new Fill({ color: '#ffffff' }) }),
      });
    }
    return cache[n];
  };
}

function weightOf(layer: CompanyLayer): (f: FeatureLike) => number {
  const s: LayerStyle = layer.style ?? {};
  if (!s.weightBy) return () => 1;
  const column = columnOf(layer)(s.weightBy);
  const lo = s.valueMin ?? 0;
  const hi = s.valueMax ?? 1;
  return (f) => {
    const n = Number(String(f.get(column) ?? '').replace(',', '.'));
    if (!Number.isFinite(n) || hi === lo) return 0.5;
    return Math.max(0, Math.min(1, (n - lo) / (hi - lo)));
  };
}

function make(layer: CompanyLayer, source: VectorSource): BaseLayer {
  const kind = kindOf(layer);
  const s: LayerStyle = layer.style ?? {};
  let made: BaseLayer;
  if (kind === 'heatmap') {
    made = new HeatmapLayer({
      source,
      radius: s.heatRadius ?? 15,
      blur: s.heatBlur ?? 15,
      weight: weightOf(layer),
      zIndex: 80,
    });
  } else if (kind === 'cluster') {
    made = new VectorLayer({
      source: new Cluster({ source, distance: s.clusterDistance ?? 40 }),
      style: clusterStyle(layer),
      zIndex: 80,
    });
  } else {
    made = new VectorLayer({
      source,
      style: styleFunction({ style: s, geometry: layer.geometry, column: columnOf(layer), label: layer.label }),
      zIndex: 80,
      declutter: !!layer.label,
    });
  }
  made.set('name', layer.name);
  made.set('companyLayer', true);
  made.set('companyKind', kind);
  made.set('featureSource', source);
  decorate(made, layer);
  return made;
}

/** What the layer says about itself to the legend, and the zooms it is drawn between. */
function decorate(l: BaseLayer, layer: CompanyLayer): void {
  const lang = (i18n.language || 'en').substring(0, 2);
  const title = layer.labels?.[lang] || layer.labels?.en || layer.key;
  l.set('legendTitle', title);
  l.set('title', title);
  l.set('legendItems', legendItems(layer.style as LayerStyle, layer.geometry, lang, i18n.t('styles.other'), title));
  // minScale: not drawn when zoomed out further; maxScale: not drawn when zoomed in further.
  l.setMinZoom(layer.minScale ? scaleToZoom(layer.minScale) : -Infinity);
  l.setMaxZoom(layer.maxScale ? scaleToZoom(layer.maxScale) : Infinity);
}

/**
 * The layer as the person sees it: their own view of it when they keep one (its style, label and scales), its main
 * look otherwise.
 */
export function effective(layer: CompanyLayer): CompanyLayer {
  const mine = layer.mine;
  if (!mine?.style) return layer;
  return { ...layer, style: mine.style, label: mine.label ?? null, minScale: mine.minScale ?? null, maxScale: mine.maxScale ?? null };
}

/** Draws the layer as the person sees it ({@link effective}). */
export function show(map: Map, layer: CompanyLayer): void {
  draw(map, effective(layer));
}

/** Draws the layer as it is given: the same layer restyled, or a new one when its kind changed. */
function draw(map: Map, layer: CompanyLayer): void {
  const existing = find(map, layer);
  if (existing && existing.get('companyKind') === kindOf(layer)) {
    const s: LayerStyle = layer.style ?? {};
    if (existing instanceof HeatmapLayer) {
      existing.setRadius(s.heatRadius ?? 15);
      existing.setBlur(s.heatBlur ?? 15);
      // the weight is fixed when the layer is made: make it again when it changes
      if (existing.get('weightBy') !== (s.weightBy ?? null)) {
        replace(map, existing, layer);
        return;
      }
    } else if (existing instanceof VectorLayer) {
      const src = existing.getSource();
      if (src instanceof Cluster) {
        src.setDistance(s.clusterDistance ?? 40);
        existing.setStyle(clusterStyle(layer));
      } else {
        existing.setStyle(styleFunction({ style: s, geometry: layer.geometry, column: columnOf(layer), label: layer.label }));
        existing.setDeclutter?.(!!layer.label);
      }
    }
    decorate(existing, layer);
    existing.setVisible(true);
    existing.changed();
    return;
  }
  if (existing) {
    replace(map, existing, layer);
    return;
  }
  const made = make(layer, sourceOf(layer));
  made.set('weightBy', layer.style?.weightBy ?? null);
  map.addLayer(made);
}

function replace(map: Map, existing: BaseLayer, layer: CompanyLayer): void {
  const source = featureSource(existing) ?? sourceOf(layer);
  const made = make(layer, source);
  made.set('weightBy', layer.style?.weightBy ?? null);
  const layers = map.getLayers();
  const at = layers.getArray().indexOf(existing);
  map.removeLayer(existing);
  if (at >= 0) layers.insertAt(at, made);
  else map.addLayer(made);
}

/** The layer drawn with a look being edited, before it is saved; {@link show} with the saved one puts it back. */
export function preview(map: Map, layer: CompanyLayer): void {
  draw(map, { ...layer, mine: null });
}

export function hide(map: Map, layer: CompanyLayer): void {
  find(map, layer)?.setVisible(false);
}

export function forget(map: Map, layer: CompanyLayer): void {
  const l = find(map, layer);
  if (l) map.removeLayer(l);
}

/** The company layers shown, by the names an analysis is asked for them by. */
export function shownNames(map: Map | null): string[] {
  const out: string[] = [];
  map?.getLayers().forEach((l) => {
    if (l.get('companyLayer') && l.getVisible()) out.push(l.get('name'));
  });
  return out;
}
